import React, { useEffect, useState } from "react";
import styles from './CreateClinicalSite.module.scss';
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import axiosClient from "../../../api/axiosClient";
import { useNavigate, useParams } from "react-router-dom";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import { useToasts } from "../../../contexts/ToastContext";
import ClinicalSiteForm from "../../../components/clinicalSiteForm/ClinicalSiteForm";
import ClinicalSiteCard from "../../../components/clinicalSiteCard/ClinicalSiteCard";
import Filter from "../../../components/filter/Filter";
import Button from "../../../components/button/Button";
import DocumentUploadModal from "../../../components/documentUploadModal/DocumentUploadModal";
import { Tab, TabPanel, Tabs } from "../../../components/tabs/Tabs";
import Icon from "../../../components/icon/Icon";
import UpdateDocumentModal from "../../../components/updateDocumentModal/UpdateDocumentModal";
import { formatPhoneNumber } from "../../../utils/stringUtils";

const CreateClinicalSite = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    siteId: '', // This corresponds to 'Clinical Site ID' input
    isActive: true, // Default to Active
    address: '',
    email: '',
    phone: '',
    website: '',
    healthSystemId: '',
    supportedYearIds: [],
    specialities: [],
    contactPerson: '',
    designation: '',
    contactPhone: '',
    contactEmail: ''
  });

  const [specialityList, setSpecialityList] = useState([]);
  const [healthSystemList, setHealthSystemList] = useState([]);
  const [yearList, setYearList] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [specialityResponse, healthSystemResponse, yearResponse] = await Promise.all([
          axiosClient.get('specialities'),
          axiosClient.get('health-systems'),
          axiosClient.get('years/details')
        ]);

        setSpecialityList(specialityResponse.data.map(item => ({
          value: item.id,
          label: item.name
        })));

        setHealthSystemList(healthSystemResponse.data.map(item => ({
          value: item.id,
          label: item.name
        })));

        const fetchedYears = Array.isArray(yearResponse.data) ? yearResponse.data : (yearResponse.data?.data || []);
        setYearList(fetchedYears.map(item => ({
          value: item.year.id,
          label: item.year.year
        })));

      } catch (error) {
        showToast(error.message, 'error');
      }
    };

    fetchData();
  }, [showToast]);

  useEffect(() => {
    const fetchSiteDetails = async () => {
      if (!siteId) return;

      setIsLoading(true);
      try {
        const response = await axiosClient.get(`clinical-sites/${siteId}`);
        const data = response.data;
        setFormData({
          name: data.name || '',
          siteId: data.siteId || '', 
          isActive: data.isActive ?? true,
          address: data.address || '',
          email: data.email || '',
          phone: data.mobileNumber ? formatPhoneNumber(data.mobileNumber) : '', 
          website: data.website || '',
          healthSystemId: data.healthSystem?.id || '',
          supportedYearIds: data.supportedYears ? data.supportedYears.map(y => y.id) : [],
          specialities: data.clinicalSiteSpecialityInfo ? data.clinicalSiteSpecialityInfo.map(s => ({
            specialityId: s.speciality?.id,
            numberOfStudents: s.numberOfStudents || ''
          })) : [],
          contactPerson: data.contactPerson || '',
          designation: data.contactPersonDesignation || '', 
          contactPhone: data.contactPersonPhone ? formatPhoneNumber(data.contactPersonPhone) : '', 
          contactEmail: data.contactPersonEmail || '' 
        });
      } catch (error) {
        showToast(error.message, 'error');
        navigate('/clinical-site'); // Redirect back on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchSiteDetails();
  }, [siteId, showToast, navigate]);

  const handleSave = async () => {
    setIsLoading(true);
    
    // Clean phone numbers
    const cleanPhone = formData.phone ? formData.phone.replace(/\D/g, '') : '';
    const cleanContactPhone = formData.contactPhone ? formData.contactPhone.replace(/\D/g, '') : '';

    try {
      if (siteId) {
        // Update uses JSON
        const payload = {
          name: formData.name,
          primaryAddress: formData.address,
          email: formData.email,
          mobileNumber: cleanPhone,
          website: formData.website,
          contactPerson: formData.contactPerson,
          contactPersonDesignation: formData.designation,
          contactPersonPhone: cleanContactPhone,
          contactPersonEmail: formData.contactEmail,
          supportedYearIds: formData.supportedYearIds,
          specialities: JSON.stringify(formData.specialities)
        };

        await axiosClient.patch(`clinical-sites/${siteId}`, payload);
        showToast('Clinical Site updated successfully');
      } else {
        // Create uses FormData (multipart/form-data)
        const formDataPayload = new FormData();
        
        if (formData.name) formDataPayload.append('name', formData.name);
        if (formData.address) formDataPayload.append('primaryAddress', formData.address);
        if (formData.email) formDataPayload.append('email', formData.email);
        if (cleanPhone) formDataPayload.append('mobileNumber', cleanPhone);
        if (formData.website) formDataPayload.append('website', formData.website);
        if (formData.healthSystemId) formDataPayload.append('healthSystemId', formData.healthSystemId);
        if (formData.contactPerson) formDataPayload.append('contactPerson', formData.contactPerson);
        if (formData.designation) formDataPayload.append('contactPersonDesignation', formData.designation);
        if (cleanContactPhone) formDataPayload.append('contactPersonPhone', cleanContactPhone);
        if (formData.contactEmail) formDataPayload.append('contactPersonEmail', formData.contactEmail);
        
        if (formData.supportedYearIds && formData.supportedYearIds.length > 0) {
          formData.supportedYearIds.forEach(id => {
            formDataPayload.append('supportedYearIds[]', id);
          });
        }
        
        if (formData.specialities && formData.specialities.length > 0) {
          formDataPayload.append('specialities', JSON.stringify(formData.specialities));
        }

        const config = {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        };

        await axiosClient.post('clinical-sites/create', formDataPayload, config);
        showToast('Clinical Site created successfully');
      }
      navigate('/clinical-site');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const [documents, setDocuments] = useState([]);
  const [documentFilter, setDocumentFilter] = useState('All Documents');
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!siteId) return;
      
      setIsDocumentsLoading(true);
      const params = {};
      
      if (documentFilter === 'Expired') {
        params.expiryStatus = 'expired';
      } else if (documentFilter === '30 Days Left') {
        params.expiryStatus = '30_days_left';
      } else if (documentFilter === '60 Days Left') {
        params.expiryStatus = '60_days_left';
      }

      try {
        const response = await axiosClient.get(`clinical-sites/${siteId}/documents`, { params });
        setDocuments(response.data);
      } catch (error) {
         console.error("Failed to load documents", error);
      } finally {
        setIsDocumentsLoading(false);
      }
    };
    fetchDocuments();
  }, [siteId, documentFilter, refreshTrigger]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <>
      <DocumentUploadModal 
        isOpen={isUploadModalOpen} 
        onClose={() => setIsUploadModalOpen(false)} 
        siteId={siteId}
        onUploadSuccess={() => {
           // Trigger document refresh
           setDocumentFilter(prev => prev); // Toggling filter or just re-fetching
           // Ideally we should have a better trigger, but since fetch depends on filter/siteId, 
           // let's just force a re-fetch by invalidating or calling a refresh function.
           // For now, I'll extract the fetch logic or use a counter.
           // Actually, simpler: define fetchDocuments outside and call it here.
           // But fetchDocuments depends on state.
           // Let's add a refresh trigger state.
           setRefreshTrigger(prev => prev + 1);
        }}
      />
      <UpdateDocumentModal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        onUpdateSuccess={() => {
          setRefreshTrigger(prev => prev + 1);
        }}
      />
      { (isLoading || isDocumentsLoading) && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Clinical Site', path: '/clinical-site' },
            { label: siteId ? formData.name : 'Create Clinical Site' }
          ]}
        />
      </div>
      
      <Tabs defaultExpanded>
         <Tab>
            <Icon name="clinic-full" className={styles.tabIcon} />
              <span>About</span>
         </Tab>
         <Tab disabled={!siteId}>
            <Icon name="clipboard" className={styles.tabIcon} />
              <span>Documents</span>
         </Tab>
         <TabPanel>
          <div className={styles.container}>
            <ClinicalSiteForm 
              formData={formData}
              specialityList={specialityList}
              healthSystemList={healthSystemList}
              yearList={yearList}
              onDataChange={(updatedData) => {
                setFormData((prevData) => {
                  return {
                    ...prevData,
                    ...updatedData
                  }
                });
              }}
              onSubmit={handleSave}
              isEdit={!!siteId}
            />
          </div>
         </TabPanel>
         <TabPanel>
          <div className={styles.container}>
             <div className={styles.topBar}>
               <div className={styles.filter}>
                 <Filter
                   filters={[
                     { label: 'All Documents', value: 'All Documents' },
                     { label: 'Expired', value: 'Expired' },
                     { label: '30 Days Left', value: '30 Days Left' },
                     { label: '60 Days Left', value: '60 Days Left' }
                   ]}
                   selected={documentFilter}
                   onChange={(filter) => setDocumentFilter(filter)}
                 />
               </div>
               <div className={styles.buttonWrapper}>
                 <Button
                   label={
                     <div className={styles.uploadButtonContent}>
                       <Icon name="arrow-up" />
                       <span>Upload New Document</span>
                     </div>
                   }
                   size="medium"
                   variant="grey"
                   onClick={() => setIsUploadModalOpen(true)}
                 />
               </div>
             </div>
             
             {documents.length > 0 ? (
               <div className={styles.cardList}>
                 {documents.map((doc) => (
                   <div key={doc.id} className={styles.cardItem}>
                     <ClinicalSiteCard
                       name={doc.fileName}
                       address={`Uploaded on ${formatDate(doc.uploadedDate)}`}
                       status={doc.agreementStatus}
                       type="document"
                       detailItems={[
                         { label: 'Activation Date', value: formatDate(doc.startDate) },
                         { label: 'Expiration Date', value: formatDate(doc.endDate) },
                         { label: 'Document Type', value: doc.isAgreement ? 'Agreement' : 'Document' }
                       ]}
                        actions={[
                          {
                            label: "Download",
                            variant: "grey",
                            onClick: () => window.open(doc.fileUrl, '_blank')
                          },
                          {
                            label: "Update Document",
                            variant: "primary",
                            onClick: () => {
                              setSelectedDocument(doc);
                              setIsUpdateModalOpen(true);
                            }
                          }
                        ]}
                      />
                   </div>
                 ))}
               </div>
              ) : (
                <div className={styles.emptyState}>
                   <p>You have no documents at this time.</p>
                </div>
              )}
          </div>
         </TabPanel>
      </Tabs>
    </>
  );
};

export default CreateClinicalSite;
